<template>
    <div class="stepper">
        <div class="stepper-header">
            <div
                class="step-number-content"
                :class="{ active: viewData.step === i}"
                :style="{ width: stepNumberContentWidth }"
                v-for="(stepItem, i) in availableSteps"
                :key="i"
            >
                <div class="step-number" :class="stepNumberClasses(i)" @click="changeStepByStepIndex(i)">
                    <span>{{ i + 1 }}</span>
                </div>
                <div lang="de">{{ stepItem.name }}</div>
            </div>
        </div>
        <div class="step-content">
            <VehicleDataFormStep
                v-show="activeStep === steps.stepVehicleData"
                :ref="steps.stepVehicleData.ref"
                :caseData="caseData"
                :vehicleManufacturers="vehicleManufacturers"
                @updateValidity="onUpdateValidity($event, steps.stepVehicleData.stepId)"
                @changeCaseData="onChangeCaseData"
            />
            <PurchaseDataFormStep
                v-show="activeStep === steps.stepPurchaseData"
                :ref="steps.stepPurchaseData.ref"
                :caseData="caseData"
                @updateValidity="onUpdateValidity($event, steps.stepPurchaseData.stepId)"
                @changeCaseData="onChangeCaseData"
                @changePurchaseContract="onChangePurchaseContract"
                @changeSalesContract="onChangeSalesContract"
            />
            <CalculatedCompensationStep
                v-show="activeStep === steps.stepCompensation"
                :caseData="caseData"
                :isFormValid="isFormValidForCalculation"
                @isCompensationPossibleChange="onChangeIsCompensationPossible"
            />
            <MandatorDataFormStep
                v-show="activeStep === steps.stepMandatorData"
                :ref="steps.stepMandatorData.ref"
                :caseData="caseData"
                @updateValidity="onUpdateValidity($event, steps.stepMandatorData.stepId)"
                @changeCaseData="onChangeCaseData"
            />
            <FinalStep
                v-show="activeStep === steps.stepFinal"
                :ref="steps.stepFinal.ref"
                :isFormValid="isFormValid"
                :caseData="caseData"
                @updateValidity="onUpdateValidity($event, steps.stepFinal.stepId)"
                @changeCaseData="onChangeCaseData"
            />
        </div>
        <PrivacyHint />
        <div class="stepper-footer">
            <button v-if="showBackButton" :disabled="viewData.isLoading" class="stepper-button button" @click="backStep">Zurück</button>
            <button v-if="showNextButton" :disabled="viewData.isLoading" class="stepper-button button-ml button success" @click="nextStep">Weiter</button>
            <button v-if="showValidateButton" :disabled="viewData.isLoading" class="stepper-button button-ml button warning" @click="validateStep">Eingabe prüfen</button>
            <button v-if="showSendButton"  :disabled="viewData.isLoading" class="stepper-button button-ml button success" @click="emitSend">Abschicken</button>
            <button v-if="showClaimCompensationButton" :disabled="viewData.isLoading" class="stepper-button button-ml button success" @click="claimCompensation">Jetzt mittleren Schadensersatz geltend machen</button>
        </div>
    </div>
</template>

<script>
import FinalStep from './steps/FinalStep';
import PrivacyHint from './PrivacyHint';
import moment from 'moment';
import TypeHelper from '../helper/TypeHelper';
import {getVehicleManufacturers} from '../api/vehicleManufacturers';
import CalculatedCompensationStep from './steps/CalculatedCompensationStep';
import MandatorDataFormStep from './steps/MandatorDataFormStep';
import VehicleDataFormStep from './steps/VehicleDataFormStep';
import PurchaseDataFormStep from './steps/PurchaseDataFormStep';

export default {
    name: 'EmissionCompensationForm',
    components: {
        PurchaseDataFormStep, MandatorDataFormStep, CalculatedCompensationStep, FinalStep, VehicleDataFormStep, PrivacyHint},
    data: () => ({
        caseData: {
            mandator_lawform:                       null,
            mandator_prename:                       null,
            mandator_surname:                       null,
            mandator_mail:                          null,
            mandator_iban:                          null,
            second_mandator_lawform:                null,
            second_mandator_prename:                null,
            second_mandator_surname:                null,
            manufacturer_id:                        null,
            purchase_date:                          null,
            emissions_type:                         null,
            mileage_purchase:                       null,
            mileage:                                null,
            price:                                  null,
            is_vehicle_sold:                        null,
            was_new:                                null,
            selected_compensation:                  null
        },
        purchaseContract: null,
        salesContract: null,
        viewData: {
            step: 0,
            isLoading: false,
            claimCompensationClicked: false,
            isCompensationPossible: true
        },
        steps: {
            stepVehicleData: {
                stepId: 1,
                name: "Daten zum Fahrzeug",
                isValid: false,
                isDirty: false,
                ref: 'vehicleDataForm'
            },
            stepPurchaseData: {
                stepId: 2,
                name: "Daten zum Kauf",
                isValid: false,
                isDirty: false,
                ref: 'purchaseDataForm'
            },
            stepCompensation: {
                stepId: 3,
                name: "Möglicher Schadensersatz",
                isValid: false
            },
            stepMandatorData: {
                stepId: 4,
                name: "Daten zum Käufer",
                isValid: false,
                isDirty: false,
                ref: 'mandatorDataForm'
            },
            stepFinal: {
                stepId: 5,
                name: "Abschicken",
                isValid: false,
                isDirty: false,
                ref: 'finalDataForm'
            }
        },
        vehicleManufacturers: []
    }),
    computed: {
        allSteps()
        {
            return [
                this.steps.stepVehicleData,
                this.steps.stepPurchaseData,
                this.steps.stepCompensation,
                this.steps.stepMandatorData,
                this.steps.stepFinal
            ];
        },
        availableSteps()
        {
            if (this.viewData.claimCompensationClicked && this.viewData.isCompensationPossible)
                return this.allSteps;
            else
            {
                return [
                    this.steps.stepVehicleData,
                    this.steps.stepPurchaseData,
                    this.steps.stepCompensation
                ];
            }
        },
        activeStep()
        {
            return this.availableSteps[this.viewData.step];
        },
        stepNumberContentWidth()
        {
            return 'calc(100%/' + this.availableSteps.length + ')'
        },
        showBackButton()
        {
            return this.viewData.step > 0;
        },
        showNextButton()
        {
            return (this.activeStep !== this.steps.stepCompensation && this.activeStep.isValid && this.viewData.step < (this.availableSteps.length - 1))
                || (this.activeStep === this.steps.stepCompensation && this.viewData.claimCompensationClicked && this.isFormValidForCalculation && this.viewData.isCompensationPossible);
        },
        showValidateButton()
        {
            return !this.activeStep.isValid && this.activeStep !== this.steps.stepCompensation;
        },
        showSendButton()
        {
            return this.activeStep === this.steps.stepFinal && this.isFormValid;
        },
        showClaimCompensationButton()
        {
            return this.activeStep === this.steps.stepCompensation
                && this.viewData.claimCompensationClicked === false
                && this.isFormValidForCalculation
                && this.viewData.isCompensationPossible;
        },
        isFormValid()
        {
            return this.steps.stepVehicleData.isValid
                && this.steps.stepPurchaseData.isValid
                && this.steps.stepMandatorData.isValid
                && this.steps.stepFinal.isValid;
        },
        isFormValidForCalculation()
        {
            return this.steps.stepVehicleData.isValid
                && this.steps.stepPurchaseData.isValid;
        }
    },
    methods: {
        stepNumberClasses(index) {
            return {
                "current-color text-white":     this.viewData.step === index,
                "success-color text-white":     this.availableSteps[index].isValid,
                "error-color text-white":       !this.availableSteps[index].isValid && this.availableSteps[index].isDirty
            };
        },
        changeStepByStepIndex(index)
        {
            if (this.activeStep.stepId !== this.steps.stepCompensation.stepId)
                this.validateStep();

            this.viewData.step = index;
        },
        nextStep()
        {
            if (this.activeStep.stepId !== this.steps.stepCompensation.stepId)
                this.validateStep();

            if (this.viewData.step < this.availableSteps.length - 1)
                this.viewData.step++;
        },
        backStep()
        {
            if (this.activeStep.stepId !== this.steps.stepCompensation.stepId)
                this.validateStep();

            if (this.viewData.step > 0)
                this.viewData.step--;
        },
        claimCompensation()
        {
            this.viewData.claimCompensationClicked = true;
            this.steps.stepCompensation.isValid = true;
            this.nextStep();
        },
        validateStep()
        {
            this.activeStep.isDirty = true;
            this.$refs[this.activeStep.ref].validate();
        },
        emitSend()
        {
            this.viewData.isLoading = true;

            this.$emit('send', {
                caseData: this.caseData,
                purchaseContract: this.purchaseContract,
                salesContract: this.salesContract
            });
        },
        onUpdateValidity(newValue, stepId)
        {
            let step = this.allSteps.find(step => step.stepId === stepId);
            step.isValid = newValue;

            if (newValue === true)
                step.isDirty = true;

            if (this.isFormValidForCalculation === false)
            {
                this.steps.stepCompensation.isValid = null;
                this.viewData.claimCompensationClicked = false;
            }
        },
        onChangeCaseData(newValue)
        {
            let me = this;
            Object.keys(newValue).forEach(property => {
                me.caseData[property] = newValue[property];
            });
        },
        onChangePurchaseContract(newValue)
        {
            this.purchaseContract = newValue;
        },
        onChangeSalesContract(newValue)
        {
            this.salesContract = newValue;
        },
        onChangeIsCompensationPossible(newValue)
        {
            this.viewData.isCompensationPossible = newValue;
        },
        async loadVehicleManufacturers()
        {
            this.viewData.isLoading = true;

            let response = await getVehicleManufacturers();

            if (response)
                this.vehicleManufacturers = response;

            this.viewData.isLoading = false;
        }
    },
    async mounted()
    {
        await this.loadVehicleManufacturers();
    },
    watch: {
        'caseData.purchase_date': function(newValue)
        {
            if (TypeHelper.isNotNullOrUndefined(newValue))
                this.caseData.purchase_date = moment(newValue).toISOString(true);
        }
    }
};
</script>

<style scoped>
.stepper .stepper-header {
    display: flex;
    justify-content: space-around;
    margin: 0 auto;
    position: relative;
}

.stepper .stepper-header::before {
    position: absolute;
    width: 100%;
    height: 1px;
    background: #DDD;
    top: 20px;
    left: 0;
    content: " ";
}

.stepper .step-number {
    background: #E9E9E9;
    border-radius: 50%;
    text-align: center;
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: auto;
    cursor: pointer;
}

.stepper .step-number-content {
    transition: transform 0.2s;
    z-index: 2;
    width: 68px;
    text-align: center;
}

.stepper .step-number-content div {
    overflow: hidden;
    white-space: break-spaces;
    word-wrap: break-word;
    overflow-wrap: break-word;

    -webkit-hyphens: auto;
    -moz-hyphens: auto;
    hyphens: auto;

    font-size: small;
}
.stepper .step-number-content.active {
    transform: scale(1.25);
}

.stepper .stepper-footer {
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;
}

.stepper .step-content div{
    margin-top: 20px;
    height: fit-content;
    min-height: 250px;
    border: 1px solid #E9E9E9;
    border-radius: 25px;
    padding-top: 10px;
    margin-left: 0px!important;
    margin-right: 0px!important;
}

.stepper .step-content {
    margin-bottom: 10px;
}

.stepper-button {
    min-width: 175px;
}

.text-white {
    color: #fff !important;
}

.success-color {
    background-color: #43AC6A !important;
}

.current-color {
    background-color: #008CBA !important;
}

.error-color {
    background-color: #f04124 !important;
}

.button-ml {
    margin-left: 10px;
}
</style>
